<template>
  <section class="apple-features" id="features-apple">
    <!-- Sticky Container (desktop/tablet) -->
    <div v-if="!isMobile" class="apple-features__sticky-container">
      <!-- Progress Indicator (only visible when section is in view) -->
      <div class="apple-features__progress" :class="{ 'is-visible': isInView }">
        <div 
          class="apple-features__progress-bar" 
          :style="{ height: `${scrollProgress * 100}%` }"
        ></div>
        <div 
          v-for="(feature, index) in features" 
          :key="index"
          class="apple-features__progress-dot"
          :class="{ 'is-active': activeFeature >= index }"
          :style="{ top: `${(index + 0.5) * (100 / features.length)}%` }"
          @click="scrollToFeature(index)"
        >
          <span class="apple-features__progress-label">{{ feature.shortName }}</span>
        </div>
      </div>

      <!-- Main Content -->
      <div class="apple-features__content">
        <!-- Feature Sections -->
        <div 
          v-for="(feature, index) in features" 
          :key="index"
          class="apple-features__section"
          :class="{ 'is-active': activeFeature === index }"
          :ref="el => sectionRefs[index] = el"
        >
          <!-- Left: Text Content -->
          <div class="apple-features__text">
            <div class="apple-features__badge" :style="{ '--badge-color': feature.color }">
              <i :class="feature.badgeIcon"></i>
              {{ feature.badge }}
            </div>
            <h2 class="apple-features__title">
              <span class="apple-features__title-line" v-for="(line, i) in feature.titleLines" :key="i">
                {{ line }}
              </span>
            </h2>
            <p class="apple-features__description">{{ feature.description }}</p>
            
            <!-- Animated Stats -->
            <div class="apple-features__stats">
              <div 
                v-for="(stat, i) in feature.stats" 
                :key="i" 
                class="apple-features__stat"
                :style="{ '--delay': `${i * 0.1}s` }"
              >
                <span class="apple-features__stat-value">{{ stat.value }}</span>
                <span class="apple-features__stat-label">{{ stat.label }}</span>
              </div>
            </div>
            
            <!-- Feature List -->
            <ul class="apple-features__list">
              <li 
                v-for="(item, i) in feature.list" 
                :key="i"
                :style="{ '--delay': `${i * 0.1 + 0.3}s` }"
              >
                <i class="pi pi-check-circle"></i>
                {{ item }}
              </li>
            </ul>
          </div>

          <!-- Right: Visual/App Demo -->
          <div class="apple-features__visual">
            <div class="apple-features__device">
              <div class="apple-features__device-frame">
                <div class="apple-features__device-notch"></div>
                <div class="apple-features__device-screen">
                  <!-- Dynamic Feature Demo -->
                  <component :is="feature.demoComponent" :active="activeFeature === index" />
                </div>
              </div>
              <!-- Floating Elements -->
              <div class="apple-features__floating" v-if="feature.floatingElements">
                <div 
                  v-for="(el, i) in feature.floatingElements" 
                  :key="i"
                  class="apple-features__floating-item"
                  :style="{ 
                    '--float-delay': `${i * 0.2}s`,
                    '--float-x': el.x,
                    '--float-y': el.y
                  }"
                >
                  <i :class="el.icon" :style="{ color: el.color }"></i>
                  <span>{{ el.text }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Fallback -->
    <div v-else class="apple-features__mobile">
      <div
        v-for="(feature, index) in features"
        :key="index"
        class="apple-features__mobile-card"
      >
        <div class="apple-features__badge" :style="{ '--badge-color': feature.color }">
          <i :class="feature.badgeIcon"></i>
          {{ feature.badge }}
        </div>
        <h2 class="apple-features__title">
          <span class="apple-features__title-line" v-for="(line, i) in feature.titleLines" :key="i">
            {{ line }}
          </span>
        </h2>
        <p class="apple-features__description">{{ feature.description }}</p>

        <div class="apple-features__stats">
          <div v-for="(stat, i) in feature.stats" :key="i" class="apple-features__stat">
            <span class="apple-features__stat-value">{{ stat.value }}</span>
            <span class="apple-features__stat-label">{{ stat.label }}</span>
          </div>
        </div>

        <ul class="apple-features__list">
          <li v-for="(item, i) in feature.list" :key="i">
            <i class="pi pi-check-circle"></i>
            {{ item }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Scroll Height Spacer -->
    <div v-if="!isMobile" class="apple-features__scroll-spacer"></div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineAsyncComponent, h, shallowRef } from 'vue';

// Demo Components for each feature
const ValidationDemo = {
  props: ['active'],
  setup(props) {
    const emails = ref([
      { email: 'john@empresa.com', status: 'checking', result: 'valid' },
      { email: 'test@temp-mail.org', status: 'checking', result: 'invalid' },
      { email: 'maria@gmail.com', status: 'checking', result: 'valid' },
      { email: 'bounce@fake.xyz', status: 'checking', result: 'invalid' },
      { email: 'carlos@startup.cl', status: 'checking', result: 'valid' },
    ]);
    
    return () => h('div', { class: 'demo-validation' }, [
      h('div', { class: 'demo-validation__header' }, [
        h('span', { class: 'demo-validation__title' }, 'Validación en Progreso'),
        h('div', { class: 'demo-validation__progress' }, [
          h('div', { class: 'demo-validation__progress-bar', style: props.active ? 'width: 100%' : 'width: 0%' })
        ])
      ]),
      h('div', { class: 'demo-validation__list' }, 
        emails.value.map((item, i) => 
          h('div', { 
            class: ['demo-validation__item', { 'is-animated': props.active }],
            style: `--delay: ${i * 0.3}s`
          }, [
            h('span', { class: 'demo-validation__email' }, item.email),
            h('span', { 
              class: ['demo-validation__status', `demo-validation__status--${item.result}`] 
            }, [
              h('i', { class: item.result === 'valid' ? 'pi pi-check' : 'pi pi-times' })
            ])
          ])
        )
      )
    ]);
  }
};

const JourneyDemo = {
  props: ['active'],
  setup(props) {
    const nodes = [
      { id: 1, type: 'trigger', label: 'Nuevo Lead', icon: 'pi pi-user-plus', x: 50, y: 20 },
      { id: 2, type: 'wait', label: 'Esperar 1 día', icon: 'pi pi-clock', x: 50, y: 35 },
      { id: 3, type: 'email', label: 'Email Bienvenida', icon: 'pi pi-envelope', x: 50, y: 50 },
      { id: 4, type: 'condition', label: '¿Abrió?', icon: 'pi pi-question', x: 50, y: 65 },
      { id: 5, type: 'email', label: 'Seguimiento', icon: 'pi pi-send', x: 25, y: 80 },
      { id: 6, type: 'tag', label: 'Marcar Activo', icon: 'pi pi-tag', x: 75, y: 80 },
    ];
    
    return () => h('div', { class: 'demo-journey' }, [
      h('svg', { class: 'demo-journey__lines', viewBox: '0 0 100 100' }, [
        h('path', { d: 'M50 25 L50 80', class: ['demo-journey__line', { 'is-animated': props.active }] }),
        h('path', { d: 'M50 70 L25 80', class: ['demo-journey__line demo-journey__line--branch', { 'is-animated': props.active }] }),
        h('path', { d: 'M50 70 L75 80', class: ['demo-journey__line demo-journey__line--branch', { 'is-animated': props.active }] }),
      ]),
      ...nodes.map((node, i) => 
        h('div', {
          class: ['demo-journey__node', `demo-journey__node--${node.type}`, { 'is-animated': props.active }],
          style: `left: ${node.x}%; top: ${node.y}%; --delay: ${i * 0.15}s`
        }, [
          h('i', { class: node.icon }),
          h('span', {}, node.label)
        ])
      )
    ]);
  }
};

const AIDemo = {
  props: ['active'],
  setup(props) {
    const texts = {
      default: "¡Hola! 👋\n\nGracias por unirte a nuestra comunidad. Estamos emocionados de tenerte aquí.\n\nEn los próximos días te enviaremos contenido exclusivo...",
      generar: "🎉 ¡Bienvenido a bordo!\n\nQueremos darte la bienvenida a nuestra familia. Prepárate para recibir las mejores ofertas y contenido exclusivo.\n\n¿Listo para empezar? 🚀",
      tono: "Estimado usuario,\n\nLe damos la más cordial bienvenida a nuestra plataforma. Agradecemos su confianza.\n\nQuedamos a su disposición para cualquier consulta.",
      corregir: "¡Hola! 👋\n\nGracias por unirte a nuestra comunidad. Estamos emocionados de tenerte aquí.\n\nEn los próximos días te enviaremos contenido exclusivo..."
    };
    
    const currentText = ref(texts.default);
    const isAnimating = ref(false);
    
    const handleClick = (type) => {
      isAnimating.value = true;
      setTimeout(() => {
        currentText.value = texts[type];
        setTimeout(() => {
          isAnimating.value = false;
        }, 100);
      }, 300);
    };
    
    return () => h('div', { class: 'demo-ai' }, [
      h('div', { class: 'demo-ai__toolbar' }, [
        h('button', { 
          class: 'demo-ai__btn', 
          onClick: () => handleClick('generar') 
        }, [h('i', { class: 'pi pi-sparkles' }), ' Generar']),
        h('button', { 
          class: 'demo-ai__btn', 
          onClick: () => handleClick('tono') 
        }, [h('i', { class: 'pi pi-sync' }), ' Tono']),
        h('button', { 
          class: 'demo-ai__btn', 
          onClick: () => handleClick('corregir') 
        }, [h('i', { class: 'pi pi-check' }), ' Corregir']),
      ]),
      h('div', { class: ['demo-ai__editor', { 'is-typing': props.active || isAnimating.value }] }, [
        h('div', { class: 'demo-ai__cursor' }),
        h('p', { class: 'demo-ai__text' }, currentText.value)
      ]),
      h('div', { class: 'demo-ai__suggestions' }, [
        h('span', { class: 'demo-ai__chip' }, '✨ Más formal'),
        h('span', { class: 'demo-ai__chip' }, '🎯 Más directo'),
        h('span', { class: 'demo-ai__chip' }, '💡 Añadir CTA'),
      ])
    ]);
  }
};

const features = shallowRef([
  {
    shortName: 'Validación',
    badge: '99% Precisión',
    badgeIcon: 'pi pi-shield',
    color: '#10b981',
    titleLines: ['Validación SMTP/DNS.', 'Cero rebotes.'],
    description: 'Un proceso de 5 pasos que incluye validación SMTP directa, chequeo de registros DNS (MX, SPF, DMARC) y detección de correos temporales.',
    stats: [
      { value: '15% → 2%', label: 'Tasa de rebote' },
      { value: '10x', label: 'Mejor deliverability' },
    ],
    list: [
      'Validación sin enviar emails',
      'Detección de trampas de spam',
      'Protección de reputación del dominio'
    ],
    demoComponent: ValidationDemo,
    floatingElements: [
      { icon: 'pi pi-check-circle', text: 'Válido', color: '#10b981', x: '85%', y: '15%' },
      { icon: 'pi pi-shield', text: 'Protegido', color: '#3b82f6', x: '-5%', y: '55%' },
    ]
  },
  {
    shortName: 'Journeys',
    badge: 'Automatización',
    badgeIcon: 'pi pi-sitemap',
    color: '#f97316',
    titleLines: ['Visual Journey Builder.', 'Tu máquina de ventas.'],
    description: 'Diseña experiencias de cliente mediante un lienzo drag-and-drop. Configura triggers, acciones y condiciones de forma visual.',
    stats: [
      { value: '24/7', label: 'Siempre activo' },
      { value: '+40%', label: 'Conversión' },
    ],
    list: [
      'Flujos automatizados ilimitados',
      'Segmentación inteligente',
      'A/B testing integrado'
    ],
    demoComponent: JourneyDemo,
    floatingElements: [
      { icon: 'pi pi-bolt', text: 'Trigger', color: '#f97316', x: '85%', y: '25%' },
      { icon: 'pi pi-chart-line', text: '+40%', color: '#10b981', x: '-5%', y: '65%' },
    ]
  },
  {
    shortName: 'IA',
    badge: 'Potenciado con IA',
    badgeIcon: 'pi pi-sparkles',
    color: '#8b5cf6',
    titleLines: ['Contenido con IA.', 'Creatividad infinita.'],
    description: 'El fin del bloqueo del escritor. Genera plantillas completas basadas en el tono y objetivo de tu campaña en segundos.',
    stats: [
      { value: '20x', label: 'Más rápido que escribir' },
      { value: '∞', label: 'Variaciones' },
    ],
    list: [
      'Generación instantánea de contenido',
      'Múltiples tonos de voz',
      'Corrección gramatical automática'
    ],
    demoComponent: AIDemo,
    floatingElements: [
      { icon: 'pi pi-sparkles', text: 'Generando...', color: '#8b5cf6', x: '85%', y: '20%' },
      { icon: 'pi pi-check', text: 'Listo', color: '#10b981', x: '-5%', y: '70%' },
    ]
  }
]);

const scrollProgress = ref(0);
const activeFeature = ref(0);
const sectionRefs = ref([]);
const isInView = ref(false);
const isMobile = ref(false);

const updateIsMobile = () => {
  isMobile.value = window.innerWidth < 768;
};

const handleScroll = () => {
  if (isMobile.value) return;
  const section = document.querySelector('.apple-features');
  if (!section) return;
  
  const rect = section.getBoundingClientRect();
  const sectionHeight = section.offsetHeight;
  const viewportHeight = window.innerHeight;
  
  // Calculate scroll progress within the section
  const scrolled = -rect.top;
  const maxScroll = sectionHeight - viewportHeight;
  const progress = Math.max(0, Math.min(1, scrolled / maxScroll));
  
  // Only show progress indicator while actively scrolling through the section
  // Hide it before section starts (rect.top > viewportHeight) or after completion (progress >= 1)
  isInView.value = rect.top < viewportHeight && rect.top > -sectionHeight + viewportHeight && progress < 0.98;
  
  // Only update progress when section is in view, keep at 1 when scrolled past
  if (rect.bottom > 0) {
    scrollProgress.value = progress;
  }
  
  // Determine active feature based on progress
  const featureCount = features.value.length;
  activeFeature.value = Math.min(
    Math.floor(progress * featureCount),
    featureCount - 1
  );
};

const scrollToFeature = (index) => {
  const section = document.querySelector('.apple-features');
  if (!section) return;
  
  const sectionTop = section.offsetTop;
  const sectionHeight = section.offsetHeight - window.innerHeight;
  const targetScroll = sectionTop + (index / features.value.length) * sectionHeight;
  
  window.scrollTo({ top: targetScroll, behavior: 'smooth' });
};

onMounted(() => {
  updateIsMobile();
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', updateIsMobile, { passive: true });
  handleScroll();
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', updateIsMobile);
});
</script>

<style lang="scss" scoped>
.apple-features {
  position: relative;
  background: linear-gradient(180deg, #0a0a0a 0%, #1a1412 100%);
  min-height: 300vh;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 80px 24px 64px;
  }
  
  &__sticky-container {
    position: sticky;
    top: 0;
    height: 100vh;
    display: flex;
    align-items: center;
    overflow: hidden;
  }
  
  &__scroll-spacer {
    height: 0;
  }

  // Mobile fallback layout
  &__mobile {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  &__mobile-card {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    padding: 24px;
    backdrop-filter: blur(10px);
  }

  @media (max-width: 768px) {
    &__title {
      font-size: 28px;
    }

    &__description {
      font-size: 15px;
    }

    &__stats {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 16px;
    }

    &__stat-value {
      font-size: 22px;
    }

    &__list {
      margin-top: 16px;
      gap: 10px;
    }
  }
  
  // Progress Indicator
  &__progress {
    position: fixed;
    left: calc(50% - 650px);
    top: 50%;
    transform: translateY(-50%);
    height: 200px;
    width: 3px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
    z-index: 100;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
    
    &.is-visible {
      opacity: 1;
      visibility: visible;
    }
    
    @media (max-width: 1400px) {
      left: 30px;
    }
    
    @media (max-width: 1024px) {
      left: 20px;
    }
  }
  
  &__progress-bar {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    background: linear-gradient(180deg, #f97316, #8b5cf6);
    border-radius: 3px;
    transition: height 0.3s ease;
  }
  
  &__progress-dot {
    position: absolute;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &.is-active {
      background: #f97316;
      box-shadow: 0 0 20px rgba(249, 115, 22, 0.5);
      transform: translate(-50%, -50%) scale(1.3);
    }
    
    &:hover .apple-features__progress-label {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  &__progress-label {
    position: absolute;
    left: 24px;
    top: 50%;
    transform: translateY(-50%) translateX(-10px);
    white-space: nowrap;
    color: white;
    font-size: 12px;
    font-weight: 500;
    opacity: 0;
    transition: all 0.3s ease;
    pointer-events: none;
  }
  
  // Content
  &__content {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 40px;
    
    @media (max-width: 1024px) {
      padding: 0 40px;
    }
    
    @media (max-width: 768px) {
      padding: 0 24px;
    }
  }
  
  &__section {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 80px;
    opacity: 0;
    transform: translateY(40px);
    pointer-events: none;
    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 0 40px;
    max-width: 1200px;
    margin: 0 auto;
    left: 50%;
    transform: translateX(-50%) translateY(40px);
    
    &.is-active {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
      pointer-events: auto;
    }
    
    @media (max-width: 1024px) {
      flex-direction: column;
      text-align: center;
      padding: 60px 24px;
      gap: 40px;
      transform: translateX(-50%) translateY(40px);
      
      &.is-active {
        transform: translateX(-50%) translateY(0);
      }
    }
  }
  
  // Text Content
  &__text {
    flex: 1;
    max-width: 500px;
    
    @media (max-width: 1024px) {
      max-width: 100%;
    }
  }
  
  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: var(--badge-color);
    padding: 8px 16px;
    border-radius: 100px;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 24px;
    backdrop-filter: blur(10px);
    
    i {
      font-size: 14px;
    }
  }
  
  &__title {
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 700;
    color: white;
    line-height: 1.1;
    margin-bottom: 24px;
  }
  
  &__title-line {
    display: block;
    
    &:last-child {
      background: linear-gradient(90deg, #f97316, #ec4899);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }
  
  &__description {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.7;
    margin-bottom: 32px;
  }
  
  &__stats {
    display: flex;
    gap: 32px;
    margin-bottom: 32px;
    
    @media (max-width: 1024px) {
      justify-content: center;
    }
  }
  
  &__stat {
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.6s ease forwards;
    animation-delay: var(--delay);
    
    .is-active & {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &__stat-value {
    display: block;
    font-size: 2rem;
    font-weight: 700;
    color: #f97316;
  }
  
  &__stat-label {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.5);
  }
  
  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      display: flex;
      align-items: center;
      gap: 12px;
      color: rgba(255, 255, 255, 0.8);
      padding: 8px 0;
      opacity: 0;
      transform: translateX(-20px);
      transition: all 0.5s ease;
      transition-delay: var(--delay);
      
      .is-active & {
        opacity: 1;
        transform: translateX(0);
      }
      
      i {
        color: #10b981;
        font-size: 18px;
      }
      
      @media (max-width: 1024px) {
        justify-content: center;
      }
    }
  }
  
  // Visual/Demo
  &__visual {
    flex: 1;
    display: flex;
    justify-content: center;
    position: relative;
  }
  
  &__device {
    position: relative;
    perspective: 1000px;
  }
  
  &__device-frame {
    width: 380px;
    height: 520px;
    background: linear-gradient(145deg, #2a2a40, #1a1a2e);
    border-radius: 40px;
    padding: 12px;
    box-shadow: 
      0 50px 100px rgba(0, 0, 0, 0.5),
      0 0 0 1px rgba(255, 255, 255, 0.1),
      inset 0 0 0 1px rgba(255, 255, 255, 0.05);
    transform: rotateY(-5deg) rotateX(5deg);
    transition: transform 0.6s ease;
    
    .is-active & {
      transform: rotateY(0) rotateX(0);
    }
    
    @media (max-width: 768px) {
      width: 300px;
      height: 420px;
    }
  }
  
  &__device-notch {
    width: 120px;
    height: 28px;
    background: #1a1a2e;
    border-radius: 0 0 20px 20px;
    margin: 0 auto 12px;
  }
  
  &__device-screen {
    height: calc(100% - 40px);
    background: #0f0f1a;
    border-radius: 28px;
    overflow: hidden;
    position: relative;
  }
  
  // Floating Elements
  &__floating {
    position: absolute;
    inset: -40px;
    pointer-events: none;
  }
  
  &__floating-item {
    position: absolute;
    left: var(--float-x);
    top: var(--float-y);
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    padding: 10px 16px;
    border-radius: 100px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
    font-size: 13px;
    font-weight: 500;
    opacity: 0;
    transform: scale(0.8);
    transition: all 0.5s ease;
    transition-delay: var(--float-delay);
    animation: float 3s ease-in-out infinite;
    animation-delay: var(--float-delay);
    
    .is-active & {
      opacity: 1;
      transform: scale(1);
    }
    
    i {
      font-size: 16px;
    }
  }
}

// Demo Styles - using flat selectors to avoid SCSS nesting issues with :deep()
:deep(.demo-validation) {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.demo-validation__header) {
  margin-bottom: 20px;
}

:deep(.demo-validation__title) {
  color: white;
  font-size: 14px;
  font-weight: 600;
  display: block;
  margin-bottom: 8px;
}

:deep(.demo-validation__progress) {
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

:deep(.demo-validation__progress-bar) {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #3b82f6);
  transition: width 2s ease;
}

:deep(.demo-validation__list) {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

:deep(.demo-validation__item) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.05);
  padding: 12px 16px;
  border-radius: 12px;
  opacity: 0;
  transform: translateX(-20px);
}

:deep(.demo-validation__item.is-animated) {
  animation: slideInCheck 0.5s ease forwards;
  animation-delay: var(--delay);
}

:deep(.demo-validation__email) {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  font-family: monospace;
}

:deep(.demo-validation__status) {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.demo-validation__status--valid) {
  background: rgba(16, 185, 129, 0.2);
  color: #10b981;
}

:deep(.demo-validation__status--invalid) {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

:deep(.demo-journey) {
  position: relative;
  height: 100%;
  padding: 20px;
}

:deep(.demo-journey__lines) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

:deep(.demo-journey__line) {
  stroke: rgba(255, 255, 255, 0.2);
  stroke-width: 2;
  fill: none;
  stroke-dasharray: 200;
  stroke-dashoffset: 200;
}

:deep(.demo-journey__line.is-animated) {
  animation: drawLine 1.5s ease forwards;
}

:deep(.demo-journey__line--branch) {
  animation-delay: 0.8s;
}

:deep(.demo-journey__node) {
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  opacity: 0;
}

:deep(.demo-journey__node.is-animated) {
  animation: popIn 0.4s ease forwards;
  animation-delay: var(--delay);
}

:deep(.demo-journey__node i) {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

:deep(.demo-journey__node span) {
  color: rgba(255, 255, 255, 0.7);
  font-size: 10px;
  white-space: nowrap;
}

:deep(.demo-journey__node--trigger i) { background: rgba(249, 115, 22, 0.2); color: #f97316; }
:deep(.demo-journey__node--wait i) { background: rgba(59, 130, 246, 0.2); color: #3b82f6; }
:deep(.demo-journey__node--email i) { background: rgba(16, 185, 129, 0.2); color: #10b981; }
:deep(.demo-journey__node--condition i) { background: rgba(139, 92, 246, 0.2); color: #8b5cf6; }
:deep(.demo-journey__node--tag i) { background: rgba(236, 72, 153, 0.2); color: #ec4899; }

:deep(.demo-ai) {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.demo-ai__toolbar) {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

:deep(.demo-ai__btn) {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(139, 92, 246, 0.2);
  border: 1px solid rgba(139, 92, 246, 0.3);
  color: #a78bfa;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

:deep(.demo-ai__btn:hover) {
  background: rgba(139, 92, 246, 0.3);
}

:deep(.demo-ai__editor) {
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px;
  position: relative;
  overflow: hidden;
}

:deep(.demo-ai__cursor) {
  position: absolute;
  width: 2px;
  height: 18px;
  background: #8b5cf6;
  opacity: 0;
}

:deep(.is-typing .demo-ai__cursor) {
  animation: blink 0.8s ease infinite;
}

:deep(.demo-ai__text) {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  opacity: 0;
}

:deep(.is-typing .demo-ai__text) {
  animation: typeIn 2s ease forwards;
}

:deep(.demo-ai__suggestions) {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}

:deep(.demo-ai__chip) {
  background: rgba(255, 255, 255, 0.1);
  padding: 6px 12px;
  border-radius: 100px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
}

// Animations
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%, 100% { transform: scale(1) translateY(0); }
  50% { transform: scale(1) translateY(-10px); }
}

@keyframes slideInCheck {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes drawLine {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes popIn {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

@keyframes typeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
